export interface StoredZipEntry {
  name: string
  data: Uint8Array
}

const centralDirectorySignature = 0x02014b50
const endOfCentralDirectorySignature = 0x06054b50
const localFileSignature = 0x04034b50
const utf8Flag = 0x0800

export function encodeStoredZip(entries: StoredZipEntry[]): Uint8Array {
  const names = new Set<string>()
  const sorted = [...entries].sort((lhs, rhs) => lhs.name.localeCompare(rhs.name))
  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  let offset = 0

  for (const entry of sorted) {
    if (!isSafePath(entry.name)) {
      throw new Error(`Archivio .framelab non valido: percorso non sicuro "${entry.name}".`)
    }
    if (names.has(entry.name)) {
      throw new Error(`Archivio .framelab non valido: voce duplicata "${entry.name}".`)
    }
    names.add(entry.name)
    const nameBytes = new TextEncoder().encode(entry.name)
    const checksum = crc32(entry.data)
    const size = entry.data.byteLength
    const localHeader = new Uint8Array(30 + nameBytes.byteLength)
    const localView = new DataView(localHeader.buffer)
    writeUint32(localView, 0, localFileSignature)
    writeUint16(localView, 4, 20)
    writeUint16(localView, 6, utf8Flag)
    writeUint16(localView, 8, 0)
    writeUint16(localView, 10, 0)
    writeUint16(localView, 12, 0)
    writeUint32(localView, 14, checksum)
    writeUint32(localView, 18, size)
    writeUint32(localView, 22, size)
    writeUint16(localView, 26, nameBytes.byteLength)
    writeUint16(localView, 28, 0)
    localHeader.set(nameBytes, 30)
    localParts.push(localHeader, entry.data)

    const centralHeader = new Uint8Array(46 + nameBytes.byteLength)
    const centralView = new DataView(centralHeader.buffer)
    writeUint32(centralView, 0, centralDirectorySignature)
    writeUint16(centralView, 4, 20)
    writeUint16(centralView, 6, 20)
    writeUint16(centralView, 8, utf8Flag)
    writeUint16(centralView, 10, 0)
    writeUint16(centralView, 12, 0)
    writeUint16(centralView, 14, 0)
    writeUint32(centralView, 16, checksum)
    writeUint32(centralView, 20, size)
    writeUint32(centralView, 24, size)
    writeUint16(centralView, 28, nameBytes.byteLength)
    writeUint16(centralView, 30, 0)
    writeUint16(centralView, 32, 0)
    writeUint16(centralView, 34, 0)
    writeUint16(centralView, 36, 0)
    writeUint32(centralView, 38, 0)
    writeUint32(centralView, 42, offset)
    centralHeader.set(nameBytes, 46)
    centralParts.push(centralHeader)

    offset += localHeader.byteLength + entry.data.byteLength
  }

  const centralSize = centralParts.reduce((total, part) => total + part.byteLength, 0)
  const centralOffset = offset
  const eocd = new Uint8Array(22)
  const eocdView = new DataView(eocd.buffer)
  writeUint32(eocdView, 0, endOfCentralDirectorySignature)
  writeUint16(eocdView, 4, 0)
  writeUint16(eocdView, 6, 0)
  writeUint16(eocdView, 8, sorted.length)
  writeUint16(eocdView, 10, sorted.length)
  writeUint32(eocdView, 12, centralSize)
  writeUint32(eocdView, 16, centralOffset)
  writeUint16(eocdView, 20, 0)

  return concatenate([...localParts, ...centralParts, eocd])
}

export function decodeStoredZip(bytes: Uint8Array): Map<string, Uint8Array> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const eocdOffset = findEndOfCentralDirectory(view)
  const entryCount = readUint16(view, eocdOffset + 10)
  const centralSize = readUint32(view, eocdOffset + 12)
  const centralOffset = readUint32(view, eocdOffset + 16)
  if (centralOffset + centralSize > eocdOffset) {
    throw new Error('Archivio .framelab non valido: directory ZIP centrale corrotta.')
  }

  const entries = new Map<string, Uint8Array>()
  let cursor = centralOffset
  for (let index = 0; index < entryCount; index += 1) {
    if (readUint32(view, cursor) !== centralDirectorySignature) {
      throw new Error('Archivio .framelab non valido: voce ZIP corrotta.')
    }
    const flags = readUint16(view, cursor + 8)
    const method = readUint16(view, cursor + 10)
    const compressedSize = readUint32(view, cursor + 20)
    const uncompressedSize = readUint32(view, cursor + 24)
    const nameLength = readUint16(view, cursor + 28)
    const extraLength = readUint16(view, cursor + 30)
    const commentLength = readUint16(view, cursor + 32)
    const localOffset = readUint32(view, cursor + 42)
    const nameStart = cursor + 46
    const nameEnd = nameStart + nameLength
    const name = decodeUTF8(bytes.subarray(nameStart, nameEnd))

    if (!isSafePath(name)) {
      throw new Error(`Archivio .framelab non valido: percorso non sicuro "${name}".`)
    }
    if (entries.has(name)) {
      throw new Error(`Archivio .framelab non valido: voce duplicata "${name}".`)
    }
    if ((flags & 1) !== 0) {
      throw new Error(`Archivio .framelab non supportato: "${name}" e' cifrato.`)
    }
    if (method !== 0 || compressedSize !== uncompressedSize) {
      throw new Error(`Archivio .framelab non supportato: "${name}" usa compressione ZIP.`)
    }

    const payload = readLocalPayload(view, bytes, localOffset, name, uncompressedSize)
    entries.set(name, payload)
    cursor = nameEnd + extraLength + commentLength
  }

  if (cursor !== centralOffset + centralSize) {
    throw new Error('Archivio .framelab non valido: directory ZIP centrale incompleta.')
  }
  return entries
}

function readLocalPayload(
  view: DataView,
  bytes: Uint8Array,
  localOffset: number,
  expectedName: string,
  size: number
): Uint8Array {
  if (readUint32(view, localOffset) !== localFileSignature) {
    throw new Error(`Archivio .framelab non valido: header locale mancante per "${expectedName}".`)
  }
  const localNameLength = readUint16(view, localOffset + 26)
  const localExtraLength = readUint16(view, localOffset + 28)
  const localNameStart = localOffset + 30
  const localNameEnd = localNameStart + localNameLength
  const localName = decodeUTF8(bytes.subarray(localNameStart, localNameEnd))
  if (localName !== expectedName) {
    throw new Error(`Archivio .framelab non valido: header locale incoerente per "${expectedName}".`)
  }
  const payloadStart = localNameEnd + localExtraLength
  const payloadEnd = payloadStart + size
  if (payloadEnd > bytes.byteLength) {
    throw new Error(`Archivio .framelab non valido: payload incompleto per "${expectedName}".`)
  }
  return bytes.slice(payloadStart, payloadEnd)
}

function findEndOfCentralDirectory(view: DataView): number {
  const minimumSize = 22
  if (view.byteLength < minimumSize) {
    throw new Error('Archivio .framelab non valido: file troppo piccolo.')
  }
  const lowerBound = Math.max(0, view.byteLength - 65_557)
  for (let offset = view.byteLength - minimumSize; offset >= lowerBound; offset -= 1) {
    if (readUint32(view, offset) === endOfCentralDirectorySignature) {
      return offset
    }
  }
  throw new Error('Archivio .framelab non valido: fine directory ZIP non trovata.')
}

function readUint16(view: DataView, offset: number): number {
  if (offset < 0 || offset + 2 > view.byteLength) {
    throw new Error('Archivio .framelab non valido: lettura ZIP fuori limite.')
  }
  return view.getUint16(offset, true)
}

function readUint32(view: DataView, offset: number): number {
  if (offset < 0 || offset + 4 > view.byteLength) {
    throw new Error('Archivio .framelab non valido: lettura ZIP fuori limite.')
  }
  return view.getUint32(offset, true)
}

function writeUint16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true)
}

function writeUint32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true)
}

function decodeUTF8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes)
}

function isSafePath(path: string): boolean {
  return path.length > 0
    && !path.startsWith('/')
    && !path.startsWith('\\')
    && !path.includes('\\')
    && !path.includes(':')
    && path.split('/').every((part) => part.length > 0 && part !== '.' && part !== '..')
}

function concatenate(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.byteLength, 0)
  const output = new Uint8Array(total)
  let offset = 0
  for (const part of parts) {
    output.set(part, offset)
    offset += part.byteLength
  }
  return output
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of data) {
    crc = (crc ^ byte) >>> 0
    for (let bit = 0; bit < 8; bit += 1) {
      crc = ((crc >>> 1) ^ (0xedb88320 & -(crc & 1))) >>> 0
    }
  }
  return (crc ^ 0xffffffff) >>> 0
}
